import { Routes } from '@angular/router';

import type { SeoRouteData } from '../core/seo/seo.types';
import { notReservedTenantSlugCanMatch } from './guards/not-reserved-tenant-slug.guard';
import { tenantPortalRoutes } from './tenant-portal.routes';

/**
 * Tenant slug-prefixed SPA routes: `{origin}/{slug}/portal/login`, `/{slug}/college`, etc.
 * Must be registered after all fixed top-level paths in `app.routes.ts`.
 */
export const tenantSlugAppRoutes: Routes = [
  {
    path: ':tenantSlug/careers/:jobSlug',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      seo: { title: 'Open role', robots: 'index, follow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/careers/public-careers-job-page.component').then(
        (m) => m.PublicCareersJobPageComponent,
      ),
  },
  {
    path: ':tenantSlug/careers',
    pathMatch: 'full',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      seo: { title: 'Careers', robots: 'index, follow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/careers/public-careers-page.component').then(
        (m) => m.PublicCareersPageComponent,
      ),
  },
  {
    path: ':tenantSlug/candidate-invite/:token',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      inviteMode: 'personal',
      seo: { title: 'Interview invitation', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/candidate-invite/candidate-apply-landing-page.component').then(
        (m) => m.CandidateApplyLandingPageComponent,
      ),
  },
  {
    path: ':tenantSlug/apply/:token',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      inviteMode: 'open',
      seo: { title: 'Apply for role', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/candidate-invite/candidate-apply-landing-page.component').then(
        (m) => m.CandidateApplyLandingPageComponent,
      ),
  },
  {
    path: ':tenantSlug/interview/:token',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      inviteMode: 'link',
      seo: { title: 'Start interview', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/interview-link/interview-link-landing-page.component').then(
        (m) => m.InterviewLinkLandingPageComponent,
      ),
  },
  {
    path: ':tenantSlug/invites/accept/:token',
    canMatch: [notReservedTenantSlugCanMatch],
    data: { seo: { title: 'Accept invitation', robots: 'noindex, nofollow' } satisfies SeoRouteData },
    loadComponent: () =>
      import('./pages/invite-accept/tenant-invite-accept-page.component').then((m) => m.TenantInviteAcceptPageComponent),
  },
  {
    path: ':tenantSlug/portal',
    canMatch: [notReservedTenantSlugCanMatch],
    children: tenantPortalRoutes,
  },
  {
    path: ':tenantSlug/college',
    canMatch: [notReservedTenantSlugCanMatch],
    loadChildren: () => import('../college/college.routes').then((m) => m.collegeRoutes),
  },
  {
    path: ':tenantSlug/company',
    canMatch: [notReservedTenantSlugCanMatch],
    loadChildren: () => import('../company/company.routes').then((m) => m.companyRoutes),
  },
  {
    path: ':tenantSlug/student',
    canMatch: [notReservedTenantSlugCanMatch],
    loadChildren: () => import('../student/student.routes').then((m) => m.studentRoutes),
  },
  {
    path: ':tenantSlug',
    pathMatch: 'full',
    canMatch: [notReservedTenantSlugCanMatch],
    data: {
      seo: { title: 'Organization portal', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./pages/slug-landing/tenant-slug-landing-page.component').then((m) => m.TenantSlugLandingPageComponent),
  },
];
